import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Plus, XCircle, Search, Edit, Trash2, Columns, Infinity, RefreshCw } from "lucide-react";
import {
  getWithTokenAPI,
  postWithTokenAPI,
  deleteWithTokenAPI,
  putWithTokenAPI,
} from "../../util/Api";

// Material-UI imports
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  Checkbox,
  Box,
  Switch,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { useTranslation } from "react-i18next";

const SubscriptionPlansSection = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [subscriptionPlans, setSubscriptionPlans] = useState({
    loading: false,
    error: null,
    plans: [],
  });

  const [showForm, setShowForm] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    planId: null,
    planName: "",
  });

  const [createLoading, setCreateLoading] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Alert states
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // MUI Table states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  // Column visibility states
  const [columnVisibility, setColumnVisibility] = useState({
    planName: true,
    duration: true,
    price: true,
    currency: true,
    features: true,
    dailyViews: true,
    dailyMessages: true,
    status: true,
    actions: true,
  });

  const [formData, setFormData] = useState({
    plan_name: "",
    duration_in_days: "",
    price: "",
    currency: "INR",
    features: [""],
    daily_views_limit: "",
    daily_messages_limit: "",
    unlimited_views: true,
    unlimited_messages: true,
    status: "active",
  });

  // Show alert function
  const showAlert = (message, severity = "success") => {
    setAlert({
      open: true,
      message,
      severity,
    });
  };

  // Check URL for create/edit mode
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get("mode");
    const id = searchParams.get("id");

    if (mode === "create") {
      setShowForm(true);
      setEditingPlanId(null);
      resetForm();
    } else if (mode === "edit" && id) {
      const plan = subscriptionPlans.plans.find((p) => p._id === id);
      if (plan) {
        setupEditForm(plan);
      }
    }
  }, [location.search, subscriptionPlans.plans]);

  // Fetch subscription plans with pagination - CORRECTED VERSION
  const fetchSubscriptionPlans = async (searchKeyword = "", limit = rowsPerPage, pageNum = 1) => {
    setSubscriptionPlans((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const token = localStorage.getItem("matrimony_token");

      if (!token) {
        throw new Error("Authentication token not found");
      }

      let url = searchKeyword.trim()
        ? `/subscriptionPlan/get_all?search=${encodeURIComponent(searchKeyword)}`
        : `/subscriptionPlan/get_all?page=${pageNum}&limit=${limit}`;

      console.log("API URL:", url);

      const response = await getWithTokenAPI(token, url);
      console.log("Full API Response:", response);

      let plans = [];

      // Handle the actual API response structure based on your provided response
      if (response.statusCode === 200 && response.data) {
        // Case 1: Response has nested data structure { data: { data: [], totalCount: X } }
        if (response.data.data && Array.isArray(response.data.data)) {
          plans = response.data.data;
          console.log("Plans from response.data.data:", plans);
        }
        // Case 2: Response has direct data array { data: [] }
        else if (Array.isArray(response.data)) {
          plans = response.data;
          console.log("Plans from response.data:", plans);
        }
        // Case 3: Response data is an object with plans array
        else if (response.data.plans && Array.isArray(response.data.plans)) {
          plans = response.data.plans;
          console.log("Plans from response.data.plans:", plans);
        }
      }
      // Case 4: Direct array response
      else if (Array.isArray(response)) {
        plans = response;
        console.log("Plans from direct array response:", plans);
      }
      // Case 5: Success field with data array
      else if (response.success && response.data && Array.isArray(response.data)) {
        plans = response.data;
        console.log("Plans from success response:", plans);
      }
      // Case 6: No data found
      else if (response.message === "No plans found" || response.message === "No subscription plans found") {
        plans = [];
        console.log("No plans found message received");
      }
      // Case 7: Unexpected response format
      else {
        console.warn("Unexpected API response format:", response);
        throw new Error(response.message || "Unexpected API response format");
      }

      // Ensure plans is always an array
      if (!Array.isArray(plans)) {
        console.warn("Plans is not an array, converting to empty array:", plans);
        plans = [];
      }

      console.log("Final plans to set:", plans);

      setSubscriptionPlans({
        loading: false,
        error: null,
        plans: plans,
      });

    } catch (error) {
      console.error("Fetch subscription plans error:", error);
      setSubscriptionPlans((prev) => ({
        ...prev,
        loading: false,
        error: error.message || "Failed to load subscription plans. Please try again.",
      }));
      showAlert(error.message || "Failed to load subscription plans", "error");
    }
  };

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  // Debug useEffect to monitor state changes
  useEffect(() => {
    console.log("Current subscription plans state:", subscriptionPlans);
  }, [subscriptionPlans]);

  // Search handler with debouncing
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPage(0);

    if (value.trim() === "") {
      fetchSubscriptionPlans("");
    } else {
      const timeoutId = setTimeout(() => {
        fetchSubscriptionPlans(value);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  };

  // Clear search and reload normal list
  const clearSearch = () => {
    setSearchTerm("");
    fetchSubscriptionPlans("");
    setPage(0);
  };

  // Delete subscription plan with confirmation
  const confirmDelete = (plan) => {
    setDeleteDialog({
      open: true,
      planId: plan._id,
      planName: plan.plan_name,
    });
  };

  const deleteSubscriptionPlan = async () => {
    const { planId } = deleteDialog;

    try {
      const token = localStorage.getItem("matrimony_token");
      const endpoint = `/subscriptionPlan/delete?id=${planId}`;

      const response = await deleteWithTokenAPI(token, endpoint);

      if (response?.error) {
        showAlert(response.message || "Failed to delete subscription plan", "error");
        return;
      }

      setSubscriptionPlans((prev) => ({
        ...prev,
        plans: prev.plans.filter((plan) => plan._id !== planId),
      }));

      showAlert("Subscription plan deleted successfully!", "success");
    } catch (error) {
      console.error("Failed to delete plan:", error);
      showAlert("Something went wrong while deleting the plan.", "error");
    } finally {
      setDeleteDialog({ open: false, planId: null, planName: "" });
    }
  };

  // Create new plan
  const handleCreatePlan = async () => {
    const token = localStorage.getItem("matrimony_token");

    if (!token) {
      showAlert("Authentication token not found. Please login again.", "error");
      return;
    }

    if (!formData.plan_name.trim()) {
      showAlert("Plan name is required", "error");
      return;
    }
    if (!token) {
      showAlert("Authentication token not found. Please login again.", "error");
      return;
    }

    if (!formData.plan_name.trim()) {
      showAlert("Plan name is required", "error");
      return;
    }

    const isFreePlan = formData.plan_name.trim().toLowerCase() === "free" || formData.plan_name.trim().toLowerCase() === "Free";

    if (!isFreePlan) {
      if (!formData.duration_in_days || formData.duration_in_days <= 0) {
        showAlert("Valid duration is required", "error");
        return;
      }

      if (!formData.price || formData.price <= 0) {
        showAlert("Valid price is required", "error");
        return;
      }
    }

    if (isFreePlan) {
      formData.duration_in_days = 0;
      formData.price = 0;
    }


    setCreateLoading(true);

    try {
      const payload = {
        plan_name: formData.plan_name,
        duration_in_days: parseInt(formData.duration_in_days),
        price: parseFloat(formData.price),
        currency: formData.currency,
        features: formData.features.filter((feature) => feature.trim() !== ""),
        daily_views_limit: formData.unlimited_views ? 0 : parseInt(formData.daily_views_limit) || 0,
        daily_messages_limit: formData.unlimited_messages ? 0 : parseInt(formData.daily_messages_limit) || 0,
        status: formData.status,
      };

      const response = await postWithTokenAPI(
        token,
        "/subscriptionPlan/create",
        payload
      );

      if (response.error) {
        throw new Error(response.message || "Failed to create plan");
      }

      if (response.statusCode === 200 || response.statusCode === 201) {
        showAlert("Subscription plan created successfully!", "success");
        resetFormAndNavigate();
        fetchSubscriptionPlans();
      } else {
        throw new Error(response.message || "Failed to create plan");
      }
    } catch (error) {
      console.error("Create plan error:", error);
      showAlert(error.message || "Failed to create subscription plan", "error");
    } finally {
      setCreateLoading(false);
    }
  };

  // Setup edit form for particular ID
  const setupEditForm = (plan) => {
    const dailyViewsLimit = plan.daily_views_limit || 0;
    const dailyMessagesLimit = plan.daily_messages_limit || 0;

    setEditingPlanId(plan._id);
    setFormData({
      plan_name: plan.plan_name || "",
      duration_in_days: plan.duration_in_days?.toString() || "",
      price: plan.price?.toString() || "",
      currency: plan.currency || "INR",
      features: plan.features && plan.features.length > 0 ? [...plan.features] : [""],
      daily_views_limit: dailyViewsLimit > 0 ? dailyViewsLimit.toString() : "",
      daily_messages_limit: dailyMessagesLimit > 0 ? dailyMessagesLimit.toString() : "",
      unlimited_views: dailyViewsLimit === 0,
      unlimited_messages: dailyMessagesLimit === 0,
      status: plan.status || "active",
    });
    setShowForm(true);

    navigate(`?mode=edit&id=${plan._id}`);
  };

  // Reset form function
  const resetForm = () => {
    setFormData({
      plan_name: "",
      duration_in_days: "",
      price: "",
      currency: "INR",
      features: [""],
      daily_views_limit: "",
      daily_messages_limit: "",
      unlimited_views: true,
      unlimited_messages: true,
      status: "active",
    });
  };

  // Reset form and navigate
  const resetFormAndNavigate = () => {
    resetForm();
    setShowForm(false);
    setEditingPlanId(null);
    navigate("?");
  };

  // Update function for particular ID
  const handleUpdatePlan = async () => {
    if (!editingPlanId) {
      showAlert("No plan selected for editing", "error");
      return;
    }

    const token = localStorage.getItem("matrimony_token");

    if (!token) {
      showAlert("Authentication token not found. Please login again.", "error");
      return;
    }

    if (!formData.plan_name.trim()) {
      showAlert("Plan name is required", "error");
      return;
    }
    const isFreePlan = formData.plan_name.trim().toLowerCase() === "free" || formData.plan_name.trim().toLowerCase() === "Free";

    if (!isFreePlan) {
      if (!formData.duration_in_days || formData.duration_in_days <= 0) {
        showAlert("Valid duration is required", "error");
        return;
      }

      if (!formData.price || formData.price <= 0) {
        showAlert("Valid price is required", "error");
        return;
      }
    }

    if (isFreePlan) {
      formData.duration_in_days = 0;
      formData.price = 0;
    }


    setEditLoading(true);

    try {
      const payload = {
        plan_name: formData.plan_name,
        duration_in_days: parseInt(formData.duration_in_days),
        price: parseFloat(formData.price),
        currency: formData.currency,
        features: formData.features.filter((feature) => feature.trim() !== ""),
        daily_views_limit: formData.unlimited_views ? 0 : parseInt(formData.daily_views_limit) || 0,
        daily_messages_limit: formData.unlimited_messages ? 0 : parseInt(formData.daily_messages_limit) || 0,
        status: formData.status,
      };

      const response = await putWithTokenAPI(
        token,
        `/subscriptionPlan/update?id=${editingPlanId}`,
        payload
      );

      if (response.error) {
        throw new Error(response.message || "Failed to update plan");
      }

      if (response.statusCode === 200 || response.success) {
        showAlert("Subscription plan updated successfully!", "success");
        resetFormAndNavigate();
        fetchSubscriptionPlans();
      } else {
        throw new Error(response.message || "Failed to update plan");
      }
    } catch (error) {
      console.error("Update plan error:", error);
      showAlert(error.message || "Failed to update subscription plan", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // Feature management functions
  const addFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, ""],
    });
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      features: newFeatures,
    });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({
      ...formData,
      features: newFeatures,
    });
  };

  // Handle unlimited toggle
  const handleUnlimitedToggle = (field) => {
    if (field === 'views') {
      setFormData(prev => ({
        ...prev,
        unlimited_views: !prev.unlimited_views,
        daily_views_limit: !prev.unlimited_views ? "" : prev.daily_views_limit
      }));
    } else if (field === 'messages') {
      setFormData(prev => ({
        ...prev,
        unlimited_messages: !prev.unlimited_messages,
        daily_messages_limit: !prev.unlimited_messages ? "" : prev.daily_messages_limit
      }));
    }
  };

  // MUI Table handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleColumnsClick = (event) => {
    setColumnsAnchorEl(event.currentTarget);
  };

  const handleColumnsClose = () => {
    setColumnsAnchorEl(null);
  };

  const handleColumnVisibilityChange = (column) => {
    setColumnVisibility(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  // NEW: Show all columns function
  const showAllColumns = () => {
    setColumnVisibility({
      planName: true,
      duration: true,
      price: true,
      currency: true,
      features: true,
      dailyViews: true,
      dailyMessages: true,
      status: true,
      actions: true,
    });
    handleColumnsClose();
  };

  // NEW: Hide all columns function
  const hideAllColumns = () => {
    setColumnVisibility({
      planName: false,
      duration: false,
      price: false,
      currency: false,
      features: false,
      dailyViews: false,
      dailyMessages: false,
      status: false,
      actions: false,
    });
    handleColumnsClose();
  };

  // Filter data based on search and filters
  const filteredData = subscriptionPlans.plans.filter((plan) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        plan.plan_name?.toLowerCase().includes(searchLower) ||
        plan.features?.some(feature => feature.toLowerCase().includes(searchLower)) ||
        plan.price?.toString().includes(searchTerm) ||
        plan.duration_in_days?.toString().includes(searchTerm)
      );
    }

    const matchesStatus =
      statusFilter === "all" ||
      plan.status === statusFilter;

    return matchesStatus;
  });

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Column configuration

  const { t } = useTranslation();

  const columns = [
    { key: 'planName', label: t("subscription.planName"), visible: columnVisibility.planName },
    { key: 'duration', label: t("subscription.duration"), visible: columnVisibility.duration },
    { key: 'price', label: t("subscription.price"), visible: columnVisibility.price },
    { key: 'currency', label: t("subscription.currency"), visible: columnVisibility.currency },
    { key: 'features', label: t("subscription.features"), visible: columnVisibility.features },
    { key: 'dailyViews', label: t("subscription.dailyViews"), visible: columnVisibility.dailyViews },
    { key: 'dailyMessages', label: t("subscription.dailyMessages"), visible: columnVisibility.dailyMessages },
    { key: 'status', label: t("subscription.status"), visible: columnVisibility.status },
    { key: 'actions', label: t("subscription.actions"), visible: columnVisibility.actions },
  ];

  const visibleColumns = columns.filter(col => col.visible);
  const visibleColumnsCount = Object.values(columnVisibility).filter(Boolean).length;
  const totalColumnsCount = columns.length;

  const { loading, error, plans } = subscriptionPlans;

  // Debug log for table rendering
  console.log("Rendering paginatedData:", paginatedData);

  return (
    <div className="space-y-6 p-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{t("subscription.title")}</h2>
          <p className="text-muted-foreground">{t("subscription.description")}</p>
        </div>
        <Button
          onClick={() => navigate("?mode=create")}
          className="flex items-center gap-2 cursor-pointer"
          disabled={loading}
        >
          <Plus className="w-4 h-4" />
          {t("subscription.addNewPlan")}
        </Button>
      </div>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          variant="filled"
        >
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Add/Edit Plan Form */}
      {showForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>
              {editingPlanId
                ? t("subscriptionform.editPlanTitle")
                : t("subscriptionform.createPlanTitle")}
            </CardTitle>

            <CardDescription>
              {editingPlanId
                ? t("subscriptionform.editPlanDesc")
                : t("subscriptionform.createPlanDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="plan_name">   {t("subscriptionform.form.planName")} *</Label>
                <Input
                  id="plan_name"
                  value={formData.plan_name}
                  onChange={(e) =>
                    setFormData({ ...formData, plan_name: e.target.value })
                  }
                  placeholder={t("subscriptionform.form.planName")}
                  disabled={createLoading || editLoading}
                />
              </div>
              <div>
                <Label htmlFor="duration_in_days">  {t("subscriptionform.form.duration")}</Label>
                <Input
                  id="duration_in_days"
                  type="number"
                  value={formData.duration_in_days}
                  onChange={(e) =>
                    setFormData({ ...formData, duration_in_days: e.target.value })
                  }
                  placeholder="e.g., 90"
                  disabled={createLoading || editLoading}
                />
              </div>
              <div>
                <Label htmlFor="price">{t("subscriptionform.form.price")}</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  placeholder="e.g., 1999"
                  disabled={createLoading || editLoading}
                />
              </div>
              <div>
                <Label htmlFor="currency"> {t("subscriptionform.form.currency")} *</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) =>
                    setFormData({ ...formData, currency: value })
                  }
                  disabled={createLoading || editLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("subscriptionform.form.selectCurrency")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INR">INR (₹)</SelectItem>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Daily Views Limit with Toggle */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="daily_views_limit">   {t("subscriptionform.form.dailyViewsLimit")}</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">   {t("subscriptionform.view.unlimited")}</span>
                    <Switch
                      checked={!formData.unlimited_views}
                      onChange={() => handleUnlimitedToggle('views')}
                      color="primary"
                      size="small"
                    />
                    <span className="text-sm text-gray-600">{t("subscriptionform.view.limited")}</span>
                  </div>
                </div>
                <Input
                  id="daily_views_limit"
                  type="number"
                  value={formData.daily_views_limit}
                  onChange={(e) =>
                    setFormData({ ...formData, daily_views_limit: e.target.value })
                  }
                  placeholder="e.g., 50"
                  disabled={formData.unlimited_views || createLoading || editLoading}
                />
                {formData.unlimited_views && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Infinity className="w-4 h-4" />
                    <span>{t("subscriptionform.view.unlimited")}</span>
                  </div>
                )}
              </div>

              {/* Daily Messages Limit with Toggle */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="daily_messages_limit">   {t("subscriptionform.form.dailyMessagesLimit")}</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">       {t("subscriptionform.view.unlimited")}</span>
                    <Switch
                      checked={!formData.unlimited_messages}
                      onChange={() => handleUnlimitedToggle('messages')}
                      color="primary"
                      size="small"
                    />
                    <span className="text-sm text-gray-600">{t("subscriptionform.view.limited")}</span>
                  </div>
                </div>
                <Input
                  id="daily_messages_limit"
                  type="number"
                  value={formData.daily_messages_limit}
                  onChange={(e) =>
                    setFormData({ ...formData, daily_messages_limit: e.target.value })
                  }
                  placeholder="e.g., 20"
                  disabled={formData.unlimited_messages || createLoading || editLoading}
                />
                {formData.unlimited_messages && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Infinity className="w-4 h-4" />
                    <span>{t("subscriptionform.view.unlimited")}</span>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="status"> {t("subscription.status")} *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) =>
                    setFormData({ ...formData, status: value })
                  }
                  disabled={createLoading || editLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Features Section */}
            <div>
              <Label> {t("subscriptionform.form.features")}</Label>
              <div className="space-y-2 mt-2">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      placeholder={`Feature ${index + 1}`}
                      disabled={createLoading || editLoading}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeFeature(index)}
                      disabled={
                        formData.features.length === 1 ||
                        createLoading ||
                        editLoading
                      }
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  onClick={addFeature}
                  disabled={createLoading || editLoading}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {t("subscriptionform.form.addFeature")}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t("subscriptionform.form.noFeatures")}
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                onClick={editingPlanId ? handleUpdatePlan : handleCreatePlan}
                disabled={editLoading || createLoading}
                className="flex items-center gap-2 cursor-pointer"
              >
                {editLoading || createLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {editingPlanId ? t("subscriptionform.buttons.updating") : t("subscriptionform.buttons.creating")}
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    {editingPlanId ? t("subscriptionform.buttons.updatePlan") : t("subscriptionform.buttons.createPlan")}
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={resetFormAndNavigate}
                    className="cursor-pointer"
                disabled={createLoading || editLoading}
              >
                {t("subscriptionform.buttons.cancel")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans List with MUI Table */}
      {!showForm && (
        <Card>
          <CardContent className="p-0">
            {/* Search and Filter Section */}
            <div className="p-4 border-b border-gray-200 bg-white table-radius">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                {/* Search Bar */}
                <div className="flex-1 w-full sm:max-w-md">
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t("userManagement.searchPlaceholder")}
                    value={searchTerm}
                    onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon className="text-gray-400" />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={clearSearch}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <XCircle className="w-4 h-4" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        width: "400px"
                      },
                    }}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Tooltip title="Filter">
                    <IconButton
                      onClick={handleFilterClick}
                      className={`border ${statusFilter !== "all"
                        ? "bg-blue-50 border-blue-200"
                        : "border-gray-300"
                        }`}
                    >
                      <FilterListIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={`Show/Hide Columns (${visibleColumnsCount}/${totalColumnsCount} visible)`}>
                    <IconButton
                      onClick={handleColumnsClick}
                      className="border border-gray-300"
                    >
                      <ViewColumnIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={() => fetchSubscriptionPlans(searchTerm, rowsPerPage, 1)}
                      className="border border-gray-300"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>

            {/* Table Section */}
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span className="ml-2">Loading subscription plans...</span>
              </div>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table sx={{ minWidth: 650 }} size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                      <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem", py: 2, width: 80 }}>
                        {t("userManagement.sno")}
                      </TableCell>
                      {visibleColumns.map((column) => (
                        <TableCell
                          key={column.key}
                          sx={{ fontWeight: "600", fontSize: "0.875rem", py: 2 }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4 }}>
                          <div className="text-gray-500 text-sm">
                            {searchTerm || statusFilter !== "all"
                              ? "No plans found for your search/filters"
                              : "No subscription plans found"}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedData.map((plan, index) => (
                        <TableRow
                          key={plan._id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            "&:hover": { backgroundColor: "#f9fafb" },
                          }}
                        >
                          <TableCell sx={{ fontSize: "0.875rem", py: 2 }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>

                          {columnVisibility.planName && (
                            <TableCell sx={{ fontSize: "0.875rem", py: 2, fontWeight: 500 }}>
                              {plan.plan_name}
                            </TableCell>
                          )}

                          {columnVisibility.duration && (
                            <TableCell sx={{ fontSize: "0.875rem", py: 2 }}>
                              {plan.duration_in_days} days
                            </TableCell>
                          )}

                          {columnVisibility.price && (
                            <TableCell sx={{ fontSize: "0.875rem", py: 2, fontWeight: 600 }}>
                              {plan.currency === "INR"
                                ? "₹"
                                : plan.currency === "USD"
                                  ? "$"
                                  : "€"}
                              {plan.price}
                            </TableCell>
                          )}

                          {columnVisibility.currency && (
                            <TableCell sx={{ fontSize: "0.875rem", py: 2 }}>
                              {plan.currency}
                            </TableCell>
                          )}

                          {columnVisibility.features && (
                            <TableCell sx={{ fontSize: "0.875rem", py: 2 }}>
                              <div className="max-w-xs">
                                {plan.features && plan.features.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {plan.features.slice(0, 2).map((feature, idx) => (
                                      <Chip
                                        key={idx}
                                        label={feature}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: "0.75rem" }}
                                      />
                                    ))}
                                    {plan.features.length > 2 && (
                                      <Chip
                                        label={`+${plan.features.length - 2} more`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: "0.75rem" }}
                                      />
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-xs">
                                    No features
                                  </span>
                                )}
                              </div>
                            </TableCell>
                          )}

                          {columnVisibility.dailyViews && (
                            <TableCell sx={{ fontSize: "0.875rem", py: 2 }}>
                              <div className="flex items-center gap-1">
                                {plan.daily_views_limit === 0 ? (
                                  <>
                                    <Infinity className="w-4 h-4 text-green-600" />
                                    <span className="text-green-600 font-medium">Unlimited</span>
                                  </>
                                ) : (
                                  <span>{plan.daily_views_limit}</span>
                                )}
                              </div>
                            </TableCell>
                          )}

                          {columnVisibility.dailyMessages && (
                            <TableCell sx={{ fontSize: "0.875rem", py: 2 }}>
                              <div className="flex items-center gap-1">
                                {plan.daily_messages_limit === 0 ? (
                                  <>
                                    <Infinity className="w-4 h-4 text-green-600" />
                                    <span className="text-green-600 font-medium">Unlimited</span>
                                  </>
                                ) : (
                                  <span>{plan.daily_messages_limit}</span>
                                )}
                              </div>
                            </TableCell>
                          )}

                          {columnVisibility.status && (
                            <TableCell sx={{ py: 2 }}>
                              <Chip
                                label={plan.status}
                                size="small"
                                color={plan.status === "active" ? "success" : "default"}
                                variant={plan.status === "active" ? "filled" : "outlined"}
                              />
                            </TableCell>
                          )}

                          {columnVisibility.actions && (
                            <TableCell sx={{ py: 2 }}>
                              <div className="flex gap-1">
                                <Tooltip title="Edit Plan">
                                  <IconButton
                                    size="small"
                                    onClick={() => setupEditForm(plan)}
                                    sx={{
                                      color: "#2563eb",
                                      "&:hover": {
                                        backgroundColor: "rgba(37, 99, 235, 0.1)",
                                      },
                                    }}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Plan">
                                  <IconButton
                                    size="small"
                                    onClick={() => confirmDelete(plan)}
                                    sx={{
                                      color: "#dc2626",
                                      "&:hover": {
                                        backgroundColor: "rgba(220, 38, 38, 0.1)",
                                      },
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    fontSize: "0.875rem",
                    "& .MuiTablePagination-toolbar": {
                      padding: 2,
                    },
                  }}
                />
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: { width: 200, p: 2 },
        }}
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status Filter
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <Button
            variant="outline"
            size="small"
            onClick={() => {
              setStatusFilter("all");
              setSearchTerm("");
              handleFilterClose();
            }}
            fullWidth
          >
            Clear Filters
          </Button>
        </div>
      </Menu>

      {/* Enhanced Columns Menu */}
      <Menu
        anchorEl={columnsAnchorEl}
        open={Boolean(columnsAnchorEl)}
        onClose={handleColumnsClose}
        PaperProps={{
          sx: { width: 240 }
        }}
      >
        {/* Header */}
        <div className="px-4 py-2 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Columns</span>
            <span className="text-xs text-gray-500">
              {visibleColumnsCount}/{totalColumnsCount}
            </span>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="px-4 py-2 border-b border-gray-200">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={showAllColumns}
              className="flex-1 text-xs h-8 cursor-pointer"
            >
              Show All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={hideAllColumns}
              className="flex-1 text-xs h-8 cursor-pointer"
            >
              Hide All
            </Button>
          </div>
        </div>

        {/* Column List */}
        <div className="max-h-64 overflow-y-auto">
          {columns.map((column) => (
            <MenuItem
              key={column.key}
              onClick={() => handleColumnVisibilityChange(column.key)}
              sx={{
                py: 1,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <Checkbox
                checked={column.visible}
                size="small"
                sx={{
                  color: '#1976d2',
                  '&.Mui-checked': {
                    color: '#1976d2',
                  },
                }}
              />
              <span className="text-sm ml-2 flex-1">{column.label}</span>
            </MenuItem>
          ))}
        </div>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, planId: null, planName: "" })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the plan "{deleteDialog.planName}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, planId: null, planName: "" })}
            variant="outline"
            className="cursor-pointer"

          >
            Cancel
          </Button>
          <Button
            onClick={deleteSubscriptionPlan}
            variant="outline"
            color="error"
            autoFocus
            className="cursor-pointer"

          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SubscriptionPlansSection;